// src/pages/Map.jsx

import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../contexts/AppContext';
import MapComponent from '../components/settings/MapComponent';
import FloatingFooter from '../components/utils/FloatingFooter';
import ConfirmModal from '../components/utils/ConfirmModal';
import InformModal from '../components/utils/InformModal';

const GOOGLE_MAP_API_KEY = 'AIzaSyD63grvHYISPK4dLfyj5kKmC6MR_wQPJ80';

// 定义一个兜底的“默认坐标”，当后端返回的 editedLanding.geoLocation 不存在时使用
const DEFAULT_LOCATION = {
  latitude: -37.751,
  longitude: 145.345,
  defaultZoom: 9
};

const Map = () => {
  const { userInfo, token, selectedRole } = useContext(AppContext);

  // 初始化为 null，表示还没从 userInfo 里拿到 landing
  const [editedLanding, setEditedLanding] = useState(null);
  const [initialLanding, setInitialLanding] = useState(null);

  /* 弹窗相关状态 */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [informOpen, setInformOpen]   = useState(false);
  const [informMsg,  setInformMsg]    = useState('');
  const [informType, setInformType]   = useState('inform'); // 'inform' | 'error'

  /* ============ 预加载：从 userInfo 拿当前 Spot 的 landing ============ */
  useEffect(() => {
    if (!userInfo || !userInfo.roleInfo || !selectedRole?.id) {
      setEditedLanding(null);
      return;
    }

    const list = userInfo.roleInfo[selectedRole.type] || [];
    const matched = list.find(r => r.id === selectedRole.id);

    // matched 可能存在，但 matched.landing 也可能不存在
    if (matched) {
      // 如果后端确实没返回 geoLocation，我们也要让 editedLanding 成功设置为 matched.landing
      const nextLanding = { ...matched.landing };
      setEditedLanding(nextLanding);
      setInitialLanding(nextLanding);
    } else {
      setEditedLanding(null);
      setInitialLanding(null);
    }
  }, [userInfo, selectedRole]);

  const hasChanges = useMemo(
    () =>
      JSON.stringify(editedLanding?.geoLocation || null) !==
      JSON.stringify(initialLanding?.geoLocation || null),
    [editedLanding, initialLanding]
  );

  /* ============ 地图坐标回调 ============ */
  const handleLocationSelect = ({ latitude, longitude, zoom }) => {
    setEditedLanding(prev => {
      // 如果 editedLanding 之前没有，直接 new 一个对象
      if (!prev) {
        return { geoLocation: { latitude, longitude, defaultZoom: zoom } };
      }
      // 否则，把 geoLocation 覆盖 / 添加进去
      return {
        ...prev,
        geoLocation: { latitude, longitude, defaultZoom: zoom }
      };
    });
  };

  /* ============ 保存到后端 ============ */
  const save = async () => {
    if (!selectedRole?.id) {
      return { ok: false, message: 'Selected role is invalid.' };
    }
    try {
      // 准备 body：后端期望的字段结构为 { landing: { ... } }
      // 注意：即使 editedLanding 里其他字段（如 homeTitle/logoImage 等）为 undefined，
      // 只要带上 geoLocation 后端就能更新
      const payload = { landing: editedLanding };

      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization : `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (resp.ok) return { ok: true };
      const data = await resp.json().catch(() => ({}));
      return { ok: false, message: data.error || resp.statusText };
    } catch (err) {
      return { ok: false, message: 'Network error' };
    }
  };

  const onConfirmSave = async () => {
    const { ok, message } = await save();
    setConfirmOpen(false);

    if (ok) {
      // 保存成功后，简单刷新页面或做其他提示
      window.location.reload();
    } else {
      setInformMsg(message);
      setInformType('error');
      setInformOpen(true);
    }
  };

  /* ============ UI 渲染 ============ */

  // 1. 如果当前角色不是 venue，则提示仅 Spot 可见
  if (selectedRole?.type !== 'venue') {
    return (
      <div className="w-[90%] max-w-7xl mx-auto px-2 mt-20">
        <p className="text-center text-gray-500">
          Venue location is only available for Spot roles.
        </p>
      </div>
    );
  }

  // 2. 如果还没加载到 editedLanding（即为 null），显示 Loading
  if (editedLanding === null) {
    return (
      <p className="text-center mt-20 text-gray-500">Loading map…</p>
    );
  }

  // 3. editedLanding 已经存在，但可能没有 geoLocation，这时不再阻止渲染 MapComponent，
  //    直接传默认坐标或空对象给 MapComponent。
  const initialGeo = editedLanding.geoLocation || DEFAULT_LOCATION;

  return (
    <div className="w-[90%] max-w-7xl mx-auto mb-20">
      <div className="mt-4 p-4 bg-white shadow-md rounded-lg">
        <h3 className="font-semibold mb-2">Click the map to set location</h3>

        <MapComponent
          apiKey={GOOGLE_MAP_API_KEY}
          initialGeolocation={initialGeo}
          onLocationSelect={handleLocationSelect}
        />
      </div>

      {/* Save 按钮（浮动在底部） */}
      <FloatingFooter hasChanges={hasChanges} onSave={() => setConfirmOpen(true)} />

      {/* 二次确认弹窗 */}
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
        header="Confirm Save"
        content="Are you sure you want to save the new location?"
      />

      {/* 成功 / 失败提示 */}
      <InformModal
        isOpen={informOpen}
        onClose={() => setInformOpen(false)}
        content={informMsg}
        type={informType}
      />
    </div>
  );
};

export default Map;
